import { useUser } from "@/zustand/store";
import {
  ArrowDownLeft,
  ArrowUpDown,
  ArrowUpRight,
  Eye,
  EyeOff,
  PlusCircle,
} from "lucide-react";
import {
  useGetCards,
  useGetCardTransactions,
  useGetRecentTransactions,
  useGetSwaps,
  useGetUser,
  useGetUserAssets,
} from "@/hooks/use-queries";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";

import { DataTable } from "@/components/data-table";
import { columns } from "./_misc/columns";
import { swapColumns } from "./_misc/swap/swap-columns";
import SwapList from "./_misc/swap/swap-list";
import PillTabsTrigger from "@/components/pill-tabs-trigger";
import { useTabParam } from "@/hooks/use-tab-param";
import TopBar from "@/components/topbar";
import { Tabs, TabsContent, TabsList } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { useSheetStore } from "@/zustand/sheetStore";
import { useModalStore } from "@/zustand/modalStore";
import TransactionList from "./_misc/TransactionList";
import CardSkeleton from "@/components/skeletons/card-skeleton";
import { cardColumns } from "./_misc/cardColumns";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import Card from "@/components/card";
import ReferralInviteCard from "@/components/referral-invite-card";

const Dashboard = () => {
  const navigate = useNavigate();
  const { openModal } = useModalStore();
  const { openSheet } = useSheetStore();
  const formatAmount = useFormatAmountWithCurrency();

  // Swap tab is there from the first paint. Only shown to users with swaps.
  const { data: swaps } = useGetSwaps();
  const recentSwaps = (swaps ?? []).slice(0, 10);
  const hasSwaps = recentSwaps.length > 0;

  const [tab, setTab] = useTabParam<"wallet" | "cards" | "swap">(
    "tab",
    "wallet",
    hasSwaps ? ["wallet", "cards", "swap"] : ["wallet", "cards"],
  );
  useGetUser();
  const { user } = useUser((state) => state);
  const { data: cards, isLoading } = useGetCards();
  const { data: transactions, isLoading: isLoadingTransactions } =
    useGetRecentTransactions();
  const [showBalance, setShowBalance] = useState(true);
  const { data: userAssets } = useGetUserAssets();
  const totalBalance = (userAssets ?? []).reduce(
    (sum, a) => sum + Number(a.balance),
    0,
  );

  const { data: cardTransactions, isLoading: isCardLoading } =
    useGetCardTransactions({
      page: 0,
      limit: 150,
    });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div>
      {/* header */}
      <TopBar title={`Hello ${user?.firstName}, 👋🏻`} />

      <div className="relative px-4">
        <div
          className={`relative mt-4 grid w-full grid-cols-1 gap-2 lg:gap-4 ${user?.canRefer ? "lg:grid-cols-3" : "lg:grid-cols-2"} `}
        >
          <div className="col-span-2 grid gap-2 lg:col-span-2 lg:grid-cols-4 lg:gap-4">
            {/* MOBILE ASSET CARDS - above My Cards */}
            <div className="col-span-full flex gap-2 lg:hidden">
              {(userAssets ?? []).map((asset) => {
                const iconMap: Record<string, string> = {
                  USDT: "/icons/usdt.svg",
                  USDC: "/icons/usdc.svg",
                };
                const networkIconMap: Record<string, string> = {
                  TRC20: "/icons/trc20.png",
                  ERC20: "/icons/erc20.svg",
                };
                const label = `${asset.token.type}-${asset.token.symbol}`;
                const icon = iconMap[asset.token.symbol] ?? "/icons/usdt.svg";
                const networkIcon = networkIconMap[asset.token.type];
                return (
                  <div
                    key={asset.id}
                    className="border-dark-stroke-5 flex flex-1 flex-col gap-1 rounded-xl border p-2.5"
                  >
                    <div className="flex items-center gap-1.5">
                      <p className="text-dark-text-300 text-[10px]">{label}</p>
                      <div className="relative size-5 shrink-0">
                        <img src={icon} alt={label} className="size-full" />
                        {networkIcon && (
                          <img
                            src={networkIcon}
                            alt={asset.token.type}
                            className="absolute -right-1 -bottom-1 size-2.5 rounded-full"
                          />
                        )}
                      </div>
                    </div>
                    <p className="text-dark-text-50 text-sm font-medium">
                      {Number(asset.balance).toFixed(2)}
                    </p>
                  </div>
                );
              })}
            </div>
            {/* MY CARDS */}
            <div
              className={`thin-scrollbar bg-dark-card-1 border-dark-stroke-5 relative h-full overflow-hidden overflow-x-auto rounded-2xl border p-4 lg:col-span-2`}
            >
              <div className="text-dark-text-50 mb-4 flex items-center justify-between">
                <span>My Cards</span>
                <button className="relative flex size-6 items-center justify-center rounded-full">
                  {/* The fill is its own layer so 30% applies to the circle
                      alone and the gold arrow stays at full strength.
                      `bg-dark-auxiliary-3` is a background-image, which the
                      `/30` opacity modifier cannot reach. */}
                  <span
                    aria-hidden="true"
                    className="bg-dark-auxiliary-3 absolute inset-0 rounded-full opacity-30"
                  />
                  {/* Masked rather than a lucide icon: the arrow takes the
                      Dark/Primary/Main *gradient*, which `stroke: currentColor`
                      cannot express. Same technique as NavIcon. */}
                  <span
                    aria-hidden="true"
                    className="icon-mask bg-dark-primary-gradient relative z-10 size-3 shrink-0"
                    style={{
                      maskImage: "url(/icons/arrow-up-right.svg)",
                      WebkitMaskImage: "url(/icons/arrow-up-right.svg)",
                    }}
                  />
                </button>
              </div>

              <div
                className={`text-dark-text-50 relative mt-4 flex gap-4 ${cards?.data.length === 1 || cards?.data.length === 0 || cards?.data.length == undefined ? "justify-center" : ""}`}
              >
                {isLoading ? (
                  <CardSkeleton />
                ) : cards?.data.length === 0 ? (
                  <div className="flex items-center justify-center">
                    <div className="flex flex-col text-center">
                      <h2 className="text-2xl font-semibold">No Cards</h2>
                      <p className="max-w-50 text-sm leading-4 font-normal">
                        Instantly create a card to start making transactions
                      </p>
                      <div className="mt-4">
                        <Button
                          className="text-dark-text-400 bg-dark-primary-main hover:bg-dark-primary-400 h-11 w-full py-1.5"
                          onClick={() => navigate("/dashboard/shop")}
                        >
                          <PlusCircle className="size-4" />
                          Buy Card
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`thin-scrollbar relative flex w-full gap-4 overflow-x-auto pb-2 ${cards?.data.length === 1 ? "justify-center" : ""}`}
                  >
                    {cards?.data.map((_, index) => (
                      <Card key={index} card={cards?.data[index]} />
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* END OF MY CARDS */}
            {/* WALLET */}
            <div className="col-start-1 row-end-2 flex flex-col lg:col-span-2 lg:col-start-3 lg:space-y-2">
              <div className="bg-dark-card-1 border-dark-stroke-5 font-dm-sans text-dark-text-50 relative flex w-full flex-col gap-3 rounded-2xl border p-4 lg:h-full lg:justify-between">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">Wallet Balance</span>
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          setShowBalance(!showBalance);
                        }}
                        variant={"ghost"}
                        className="size-auto cursor-pointer !p-1 hover:bg-white/10"
                      >
                        {showBalance ? (
                          <EyeOff className="text-dark-text-50/40 size-4" />
                        ) : (
                          <Eye className="text-dark-text-50/40 size-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xl font-medium">
                      {showBalance ? `${formatAmount(totalBalance)}` : "****"}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/dashboard/wallet")}
                    className="relative hidden size-6 items-center justify-center rounded-full lg:flex"
                  >
                    <span
                      aria-hidden="true"
                      className="bg-dark-auxiliary-3 absolute inset-0 rounded-full opacity-30"
                    />
                    <span className="relative z-10 flex">
                      <span
                        aria-hidden="true"
                        className="icon-mask bg-dark-primary-gradient size-3 shrink-0"
                        style={{
                          maskImage: "url(/icons/arrow-up-right.svg)",
                          WebkitMaskImage: "url(/icons/arrow-up-right.svg)",
                        }}
                      />
                    </span>
                  </button>
                </div>

                {/* Desktop asset cards */}
                <div className="hidden w-full gap-2 lg:flex">
                  {(userAssets ?? []).map((asset) => {
                    const iconMap: Record<string, string> = {
                      USDT: "/icons/usdt.svg",
                      USDC: "/icons/usdc.svg",
                    };
                    const networkIconMap: Record<string, string> = {
                      TRC20: "/icons/trc20.png",
                      ERC20: "/icons/erc20.svg",
                    };
                    const label = `${asset.token.type}-${asset.token.symbol}`;
                    const icon =
                      iconMap[asset.token.symbol] ?? "/icons/usdt.svg";
                    const networkIcon = networkIconMap[asset.token.type];
                    return (
                      <div
                        key={asset.id}
                        className="border-dark-stroke-5 flex flex-1 flex-col gap-1 rounded-xl border p-2.5"
                      >
                        <div className="flex items-center gap-1.5">
                          <p className="text-dark-text-300 text-[10px]">
                            {label}
                          </p>
                          <div className="relative size-5 shrink-0">
                            <img src={icon} alt={label} className="size-full" />
                            {networkIcon && (
                              <img
                                src={networkIcon}
                                alt={asset.token.type}
                                className="absolute -right-1 -bottom-1 size-2.5 rounded-full"
                              />
                            )}
                          </div>
                        </div>
                        <p className="text-dark-text-50 text-sm font-medium">
                          {Number(asset.balance).toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-dark-card-1 border-dark-stroke-5 font-dm-sans hidden justify-between space-x-1.5 rounded-2xl border px-3 py-3 *:grow lg:flex">
                <Button
                  onClick={() => openModal("walletAutoDeposit")}
                  className="hover:bg-dark-card-3 text-dark-text-50 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 transition-all duration-300"
                >
                  <div className="text-dark-text-400 bg-dark-primary-main flex size-7 items-center justify-center rounded-full">
                    <ArrowDownLeft className="size-4" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs font-medium">Deposit</p>
                </Button>
                <Button
                  onClick={() => openSheet("withdraw", 2)}
                  className="hover:bg-dark-card-3 text-dark-text-50 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 transition-all duration-300"
                >
                  <div className="text-dark-text-400 bg-dark-primary-main flex size-7 items-center justify-center rounded-full">
                    <ArrowUpRight className="size-4" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs font-medium">Withdraw</p>
                </Button>
                <Button
                  onClick={() => openModal("transfer")}
                  className="hover:bg-dark-card-3 text-dark-text-50 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 transition-all duration-300"
                >
                  <div className="text-dark-text-400 bg-dark-primary-main flex size-7 items-center justify-center rounded-full">
                    <ArrowUpDown
                      className="size-4 rotate-45"
                      strokeWidth={1.5}
                    />
                  </div>
                  <p className="text-xs font-medium">Transfer</p>
                </Button>
              </div>
            </div>
            {/* END OF WALLET */}
          </div>
          {/* REFERRAL INVITE */}
          {user?.canRefer && (
            <ReferralInviteCard
              code={user?.id}
              className="col-span-2 lg:col-span-1"
            />
          )}
          <div className="bg-dark-card-1 border-dark-stroke-5 font-dm-sans col-span-2 flex w-full justify-between space-x-1.5 rounded-2xl border px-3 py-2 *:grow lg:hidden">
            <Button
              onClick={() => openModal("walletAutoDeposit")}
              className="hover:bg-dark-card-3 text-dark-text-50 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 transition-all duration-300"
            >
              <div className="text-dark-text-400 bg-dark-primary-main flex size-7 items-center justify-center rounded-full">
                <ArrowDownLeft className="size-4" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-medium">Deposit</p>
            </Button>
            <Button
              onClick={() => openSheet("withdraw", 2)}
              className="hover:bg-dark-card-3 text-dark-text-50 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 transition-all duration-300"
            >
              <div className="text-dark-text-400 bg-dark-primary-main flex size-7 items-center justify-center rounded-full">
                <ArrowUpRight className="size-4" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-medium">Withdraw</p>
            </Button>
            <Button
              onClick={() => openModal("transfer")}
              className="hover:bg-dark-card-3 text-dark-text-50 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 transition-all duration-300"
            >
              <div className="text-dark-text-400 bg-dark-primary-main flex size-7 items-center justify-center rounded-full">
                <ArrowUpDown className="size-4 rotate-45" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-medium">Transfer</p>
            </Button>
          </div>
        </div>

        <div className="bg-dark-card-3 text-dark-text-50 mt-4 rounded-2xl backdrop-blur-sm">
          <div className="bg-dark-background-alternate rounded-tl-2xl rounded-tr-2xl p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium">Recent Transactions</h2>
              <Link
                to="/dashboard/transactions"
                className="text-dark-text-50 text-xs hover:underline"
              >
                See All
              </Link>
            </div>
          </div>

          <div className="p-4">
            <Tabs
              onValueChange={(value) => {
                setTab(value as "wallet" | "cards" | "swap");
              }}
              value={tab}
              className="bg-transparent py-4"
            >
              <TabsList className="border-dark-stroke-5 bg-dark-background-secondary h-10 w-fit rounded-full border p-1">
                <PillTabsTrigger value="wallet" active={tab === "wallet"}>
                  Wallet
                </PillTabsTrigger>
                <PillTabsTrigger value="cards" active={tab === "cards"}>
                  Cards
                </PillTabsTrigger>
                {hasSwaps && (
                  <PillTabsTrigger value="swap" active={tab === "swap"}>
                    Swap
                  </PillTabsTrigger>
                )}
              </TabsList>
              <TabsContent value="wallet" className="mt-2">
                <div className="hidden lg:block">
                  <DataTable
                    columns={columns}
                    data={transactions ?? []}
                    isLoading={isLoadingTransactions}
                  />
                </div>
                <div className="lg:hidden">
                  <TransactionList
                    isLoading={isLoadingTransactions}
                    transactions={transactions ?? []}
                  />
                </div>
              </TabsContent>
              <TabsContent value="cards" className="mt-2">
                <div className="hidden lg:block">
                  <DataTable
                    columns={cardColumns}
                    data={cardTransactions?.data.slice(0, 10) ?? []}
                    isLoading={isCardLoading}
                  />
                </div>
                <div className="lg:hidden">
                  <TransactionList
                    type="cards"
                    isLoading={isCardLoading}
                    transactions={cardTransactions?.data.slice(0, 10) ?? []}
                  />
                </div>
              </TabsContent>
              {hasSwaps && (
                <TabsContent value="swap" className="mt-2">
                  <div className="hidden lg:block">
                    <DataTable columns={swapColumns} data={recentSwaps} />
                  </div>
                  <div className="lg:hidden">
                    <SwapList swaps={recentSwaps} />
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
